import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PrintIcon from "@mui/icons-material/Print";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

toast.configure();

const todayString = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};
const dateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};
const num = (value) => Number.parseFloat(value || 0);
const money = (value) => Number(num(value).toFixed(2));
const displayMoney = (value) => money(value).toFixed(2);

const emptyHeader = {
  estimatedate: todayString(),
  jobcardid: null,
  partyid: null,
  customername: "",
  customercode: "",
  address1: "",
  address2: "",
  address3: "",
  vehicleno: "",
  mobileno: "",
  insurancecompany: "",
  policyno: "",
  claimno: "",
  labourCharge: 0,
  partsdiscrate: 0,
  lubdiscrate: 0,
  batterydiscrate: 0,
  labourdiscrate: 0,
};

const buildRow = ({ item, qty, rate, rowid, serial }) => {
  const saleqty = money(qty);
  const salerate = money(rate || item?.salerate);
  const grossvalue = money(saleqty * salerate);
  const discountrate = 0;
  const discountvalue = 0;
  const taxablevalue = grossvalue;
  const gstrate = money(item?.gstrate);
  const gstvalue = money((taxablevalue * gstrate) / 100);
  return {
    rowid: rowid || `${item.itemid}-${Date.now()}`,
    serial,
    itemid: item.itemid,
    itemcode: item.itemcode,
    itemname: item.itemname,
    partdetails: item.partdetails || `${item.itemcode} - ${item.itemname}`,
    hsncode: item.hsncode,
    rackname: item.rackname || "",
    saleqty,
    qty: saleqty,
    mrp: money(item.mrp),
    salerate,
    rate: salerate,
    gstrate,
    grossvalue,
    discountrate,
    discountvalue,
    taxablevalue,
    gstvalue,
    netvalue: money(taxablevalue + gstvalue),
    closingqty: money(item.closingqty || item.purchaseqty),
  };
};

const renumberRows = (rows) =>
  rows.map((row, index) => ({
    ...row,
    serial: index + 1,
  }));

const Estimate = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const [mode, setMode] = useState(1);
  const allmodes = [
    { value: 1, label: "New" },
    { value: 2, label: "Edit" },
    { value: 3, label: "View" },
  ];
  const [estimateType, setEstimateType] = useState("workshop");
  const [header, setHeader] = useState(emptyHeader);
  const [rows, setRows] = useState([]);
  const [jobcards, setJobcards] = useState([]);
  const [parties, setParties] = useState([]);
  const [selectedJobcard, setSelectedJobcard] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newModePartNo, setNewModePartNo] = useState("");
  const [partno, setPartno] = useState(null);
  const [itemQty, setItemQty] = useState(1);
  const [itemRate, setItemRate] = useState("");
  const [stock, setStock] = useState(0);
  const [delItem, setDelItem] = useState("");
  const [altFlag, setAltFlag] = useState("");
  const [altparts, setAltparts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadedEstimate, setLoadedEstimate] = useState(null);
  const newPartNoRef = useRef(null);
  const quantityRef = useRef(null);
  const [searchFilters, setSearchFilters] = useState({
    fromdate: todayString(),
    todate: todayString(),
    estimateno: "",
    estimatetype: "",
    text: "",
  });
  const [searchRows, setSearchRows] = useState([]);

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      sptoken: userToken,
    }),
    [userToken],
  );
  const numericMode = parseInt(mode, 10);
  const isViewMode = numericMode === 3;

  useEffect(() => {
    if (!userToken) return;
    getInitialData();
  }, [userToken]);

  useEffect(() => {
    if (loadedEstimate) return;
    if (estimateType === "workshop") {
      setSelectedParty(null);
      setHeader((prev) => ({
        ...prev,
        partyid: selectedJobcard?.partyid || null,
      }));
    } else {
      setSelectedJobcard(null);
      setHeader((prev) => ({
        ...emptyHeader,
        estimatedate: prev.estimatedate,
      }));
    }
  }, [estimateType, loadedEstimate, selectedJobcard]);

  const getInitialData = async () => {
    try {
      const [jobRes, partyRes, finRes] = await Promise.all([
        fetch("/estimate/jobcards", { headers: authHeaders }),
        fetch("/estimate/parties", { headers: authHeaders }),
        fetch("/getLoginFin", { headers: authHeaders }),
      ]);
      setJobcards(await jobRes.json());
      setParties(await partyRes.json());
      const finyear = await finRes.json();
      if (finyear?.fromdate && finyear?.todate) {
        setSearchFilters((prev) => ({
          ...prev,
          fromdate: dateInputValue(finyear.fromdate),
          todate: dateInputValue(finyear.todate),
        }));
      }
    } catch (err) {
      toast.error("Failed to load estimate masters");
      console.error(err.message);
    }
  };

  const totals = useMemo(() => {
    const netItems = rows.reduce((sum, row) => sum + num(row.netvalue), 0);
    const gross = rows.reduce((sum, row) => sum + num(row.grossvalue), 0);
    const gst = rows.reduce((sum, row) => sum + num(row.gstvalue), 0);
    const labour = estimateType === "workshop" ? num(header.labourCharge) : 0;
    const preRound = money(netItems + labour);
    const billAmt = Math.round(preRound);
    return {
      gross: money(gross),
      gst: money(gst),
      roundOff: money(billAmt - preRound),
      billAmt: money(billAmt),
    };
  }, [rows, header.labourCharge, estimateType]);

  const resetForm = () => {
    setMode(1);
    setHeader(emptyHeader);
    setRows([]);
    setSelectedJobcard(null);
    setSelectedParty(null);
    clearItemEntry();
    setAltparts([]);
    setLoadedEstimate(null);
  };

  const clearItemEntry = () => {
    setSelectedItem(null);
    setPartno(null);
    setNewModePartNo("");
    setItemQty(1);
    setItemRate("");
    setStock(0);
    setDelItem("");
    setAltFlag("");
  };

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleJobcardChange = (jobcard) => {
    setSelectedJobcard(jobcard);
    setHeader((prev) => ({
      ...prev,
      jobcardid: jobcard?.jobcardid || null,
      partyid: jobcard?.partyid || null,
      customername: jobcard?.customername || "",
      customercode: jobcard?.subledgercode || "",
      address1: jobcard?.address1 || "",
      address2: jobcard?.address2 || "",
      address3: jobcard?.address3 || "",
      vehicleno: jobcard?.vehicleno || "",
      mobileno: jobcard?.mobileno || "",
    }));
  };

  const handlePartyChange = (party) => {
    setSelectedParty(party);
    setHeader((prev) => ({
      ...prev,
      partyid: party?.subledgerid || null,
      customername: party?.subledgername || "",
      customercode: party?.subledgercode || "",
      address1: party?.address1 || "",
      address2: party?.address2 || "",
      address3: party?.address3 || "",
      partsdiscrate: party?.partsdiscrate || 0,
      lubdiscrate: party?.lubdiscrate || 0,
      batterydiscrate: party?.batterydiscrate || 0,
    }));
  };

  const applySelectedPart = (item) => {
    setSelectedItem(item);
    setPartno(item);
    setNewModePartNo(item?.itemcode || "");
    setItemRate(item?.salerate || "");
    setStock(money(item?.closingqty || item?.purchaseqty || 0));
    setDelItem(item?.deleteditem ? "YES" : "NO");
    setAltFlag(item?.altflag ? "YES" : "NO");
  };

  const handlePartnoKey = async (event) => {
    if (event.key !== "Tab") return;
    if (!newModePartNo?.trim()) return;
    event.preventDefault();
    try {
      const response = await fetch("/allparts/detailfetch", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ code: newModePartNo.trim() }),
      });
      const data = await response.json();
      if (!data?.itemid) {
        toast.error("No Part Found!");
        clearItemEntry();
        setTimeout(() => newPartNoRef.current?.focus(), 0);
        return;
      }
      applySelectedPart(data);
      setAltparts([]);
      setTimeout(() => quantityRef.current?.focus(), 0);
    } catch (err) {
      toast.error("Failed to fetch part details.");
      console.error(err.message);
    }
  };

  const handleAlternateItem = (item) => {
    const mappedItem = {
      itemid: item.alternateitemid || item.itemid,
      itemcode: item.itemcode || item.alternateitemcode,
      itemname: item.itemname || item.alternateitemname,
      partdetails:
        item.partdetails ||
        `${item.itemcode || item.alternateitemcode || ""} - ${
          item.itemname || item.alternateitemname || ""
        }`,
      mrp: item.mrp,
      salerate: item.salerate,
      gstrate: item.gstrate,
      hsncode: item.hsncode,
      rackname: item.rackname,
      closingqty: item.closingqty || item.purchaseqty,
      purchaseqty: item.closingqty || item.purchaseqty,
      deleteditem: item.deleteditem,
      altflag: item.altflag,
    };
    applySelectedPart(mappedItem);
    setAltparts([]);
    setTimeout(() => quantityRef.current?.focus(), 0);
  };

  const handleQtyKeyDown = async (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      handleAddItem();
      setTimeout(() => newPartNoRef.current?.focus(), 0);
      return;
    }
    if (event.key === "F2" && partno?.itemid) {
      event.preventDefault();
      try {
        const response = await fetch(`/altitemstock/${partno.itemid}`, {
          headers: authHeaders,
        });
        const data = await response.json();
        setAltparts(Array.isArray(data) ? data : []);
        if (!Array.isArray(data) || !data.length) {
          toast.info("No alternate item found.");
        }
      } catch (err) {
        toast.error("Failed to fetch alternate items.");
        console.error(err.message);
      }
    }
  };

  const handleAddItem = () => {
    const item = partno || selectedItem;
    if (!item) {
      toast.error("Select part no.");
      return;
    }
    if (num(itemQty) <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }
    const row = buildRow({
      item,
      qty: itemQty,
      rate: itemRate,
      serial: rows.length + 1,
    });
    setRows((prev) => renumberRows([...prev, row]));
    clearItemEntry();
  };

  const recalcEditedRow = (row, changes) => {
    const merged = { ...row, ...changes };
    return buildRow({
      item: {
        ...merged,
        salerate: merged.salerate,
        gstrate: merged.gstrate,
        mrp: merged.mrp,
      },
      qty: merged.saleqty,
      rate: merged.salerate,
      rowid: merged.rowid,
      serial: merged.serial,
    });
  };

  const detailColumns = [
    {
      field: "action",
      type: "actions",
      headerName: "",
      width: 44,
      getActions: ({ id }) =>
        isViewMode
          ? []
          : [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => setRows((prev) => renumberRows(prev.filter((row) => row.rowid !== id)))}
                color="inherit"
              />,
            ],
    },
    { field: "serial", headerName: "SRLNO", width: 72 },
    { field: "itemcode", headerName: "PART NO.", width: 140 },
    { field: "itemname", headerName: "PART DESC", flex: 1, minWidth: 220 },
    {
      field: "saleqty",
      headerName: "QTY",
      width: 90,
      type: "number",
      editable: !isViewMode,
      valueSetter: (params) => {
        const value = num(params.value);
        if (value <= 0) {
          toast.error("Quantity must be greater than zero.");
          return params.row;
        }
        const updated = recalcEditedRow(params.row, { saleqty: value });
        setRows((prev) => prev.map((row) => (row.rowid === params.row.rowid ? updated : row)));
        return updated;
      },
    },
    {
      field: "salerate",
      headerName: "RATE",
      width: 110,
      type: "number",
      editable: !isViewMode,
      valueSetter: (params) => {
        const value = num(params.value);
        if (value <= 0) {
          toast.error("Rate must be greater than zero.");
          return params.row;
        }
        const updated = recalcEditedRow(params.row, { salerate: value });
        setRows((prev) => prev.map((row) => (row.rowid === params.row.rowid ? updated : row)));
        return updated;
      },
    },
    {
      field: "grossvalue",
      headerName: "GROSS AM",
      width: 120,
      valueGetter: (params) => displayMoney(params.row.grossvalue),
    },
    {
      field: "gstrate",
      headerName: "GST %",
      width: 90,
      type: "number",
    },
    {
      field: "gstvalue",
      headerName: "GST RATE",
      width: 110,
      valueGetter: (params) => displayMoney(params.row.gstvalue),
    },
    {
      field: "netvalue",
      headerName: "NET AMT",
      width: 120,
      valueGetter: (params) => displayMoney(params.row.netvalue),
    },
  ];

  const searchColumns = [
    { field: "estimateno", headerName: "Estimate No", width: 150 },
    { field: "estimatedate", headerName: "Date", width: 120 },
    { field: "estimatetype", headerName: "Type", width: 110 },
    {
      field: "party",
      headerName: "Customer/Subledger",
      flex: 1,
      minWidth: 260,
      valueGetter: (params) =>
        params.row.estimatetype === "workshop"
          ? `${params.row.jobcustomername || ""}`
          : `${params.row.subledgercode || ""} - ${params.row.subledgername || ""}`,
    },
    { field: "vehicleno", headerName: "Vehicle No", width: 130 },
    { field: "mobileno", headerName: "Mobile No", width: 130 },
    { field: "totbillamt", headerName: "Total", width: 110 },
    {
      field: "load",
      type: "actions",
      headerName: "Edit",
      width: 80,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={<SearchIcon />}
          label="Load"
          onClick={() => loadEstimate(row.estheaderid, parseInt(mode, 10) === 3 ? 3 : 2)}
          color="primary"
        />,
      ],
    },
  ];

  const runSearch = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      const res = await fetch(`/estimate/search?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await res.json();
      setSearchRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to search estimates");
      console.error(err.message);
    }
  };

  const loadEstimate = async (estheaderid, loadMode = 2) => {
    try {
      const res = await fetch(`/estimate/data/${estheaderid}`, {
        headers: authHeaders,
      });
      const data = await res.json();
      if (!data.headerdata) {
        toast.error("Estimate not found.");
        return;
      }
      const h = data.headerdata;
      const type = h.estimatetype || "wholesale";
      setEstimateType(type);
      setHeader({
        estimatedate: h.estimatedate || todayString(),
        jobcardid: h.jobcardid,
        partyid: h.partyid,
        customername: h.jobcustomername || h.subledgername || "",
        customercode: h.subledgercode || "",
        address1: h.jobaddress1 || "",
        address2: h.jobaddress2 || "",
        address3: h.jobaddress3 || "",
        vehicleno: h.vehicleno || h.jobcarregno || "",
        mobileno: h.mobileno || "",
        insurancecompany: h.insurancecompany || "",
        policyno: h.policyno || "",
        claimno: h.claimno || "",
        labourCharge: h.totlabourcharge || 0,
        partsdiscrate: h.partsdiscrate || 0,
        lubdiscrate: h.lubdiscrate || 0,
        batterydiscrate: h.batterydiscrate || 0,
        labourdiscrate: h.labourdiscrate || 0,
      });
      setSelectedJobcard(
        type === "workshop"
          ? jobcards.find((job) => String(job.jobcardid) === String(h.jobcardid)) || null
          : null,
      );
      setSelectedParty(
        type === "wholesale"
          ? parties.find((party) => String(party.subledgerid) === String(h.partyid)) || null
          : null,
      );
      setRows(
        data.detaildata.map((row) => ({
          rowid: String(row.estdetailid),
          serial: row.serial,
          itemid: row.itemid,
          itemcode: row.itemcode,
          itemname: row.itemname,
          partdetails: row.partdetails,
          hsncode: row.hsncode,
          rackname: row.rackname,
          saleqty: money(row.saleqty),
          qty: money(row.saleqty),
          mrp: money(row.mrp),
          salerate: money(row.salerate),
          rate: money(row.salerate),
          gstrate: money(row.gstrate),
          grossvalue: money(row.grossvalue),
          discountrate: money(row.discountrate),
          discountvalue: money(row.discountvalue),
          taxablevalue: money(row.taxablevalue),
          gstvalue: money(row.gstvalue),
          netvalue: money(row.netvalue),
        })),
      );
      setLoadedEstimate(h);
      setMode(loadMode);
      clearItemEntry();
      setAltparts([]);
    } catch (err) {
      toast.error("Failed to load estimate.");
      console.error(err.message);
    }
  };

  const validateBeforeSave = () => {
    if (estimateType === "workshop" && !header.jobcardid) {
      toast.error("Select job card.");
      return false;
    }
    if (estimateType === "wholesale" && !header.partyid) {
      toast.error("Select subledger.");
      return false;
    }
    if (!rows.length) {
      toast.error("Add at least one item.");
      return false;
    }
    return true;
  };

  const saveEstimate = async () => {
    if (parseInt(mode, 10) === 3) {
      toast.error("View mode cannot save.");
      return;
    }
    if (!validateBeforeSave()) return;
    setSaving(true);
    try {
      const payload = {
        estimateType,
        header,
        rows,
      };
      const url =
        parseInt(mode, 10) === 2 && loadedEstimate?.estheaderid
          ? `/estimate/${loadedEstimate.estheaderid}`
          : "/estimate/save";
      const res = await fetch(url, {
        method: parseInt(mode, 10) === 2 && loadedEstimate?.estheaderid ? "PUT" : "POST",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.type === "error") {
        toast.error(data.message);
        return;
      }
      toast.success(data.message);
      setLoadedEstimate({
        estheaderid: data.estheaderid,
        estimateno: data.estimateno,
      });
      setMode(2);
      runSearch();
    } catch (err) {
      toast.error("Failed to save estimate.");
      console.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const printEstimate = async () => {
    if (!loadedEstimate?.estheaderid) {
      toast.error("Save or load an estimate first.");
      return;
    }
    try {
      const response = await fetch(`/estimate/print/${loadedEstimate.estheaderid}`, {
        method: "GET",
        headers: { sptoken: userToken },
      });
      if (!response.ok) {
        toast.error("Failed to print estimate.");
        return;
      }
      const pdfBlob = await response.blob();
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    } catch (err) {
      toast.error("Failed to print estimate.");
      console.error(err.message);
    }
  };

  const searchFilterChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!userToken) {
    return <Redirect exact to="/sparepartsLogin" />;
  }

  return (
    <div>
      <Sidebar
        Title="ESTIMATE"
        expandedPanel="panel1"
        children={
          <Box sx={{ width: "100%", p: 1 }}>
            <Paper sx={{ p: 1.5, borderRadius: 1, mb: 1.5 }} elevation={2}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1}>
                <FormControl size="small" sx={{ width: 130 }}>
                  <InputLabel>Mode</InputLabel>
                  <Select
                    label="Mode"
                    value={mode}
                    onChange={(e) => {
                      setMode(e.target.value);
                      if (parseInt(e.target.value, 10) === 1) resetForm();
                    }}
                  >
                    {allmodes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ width: 190 }}>
                  <InputLabel>Estimate Type</InputLabel>
                  <Select
                    label="Estimate Type"
                    value={estimateType}
                    disabled={Boolean(loadedEstimate)}
                    onChange={(e) => setEstimateType(e.target.value)}
                  >
                    <MenuItem value="workshop">Estimate For Workshop</MenuItem>
                    <MenuItem value="wholesale">Estimate For Wholesale</MenuItem>
                  </Select>
                </FormControl>

                {loadedEstimate?.estimateno && (
                  <TextField
                    size="small"
                    label="Estimate No"
                    value={loadedEstimate.estimateno}
                    sx={{ width: 170 }}
                    InputProps={{ readOnly: true }}
                  />
                )}

                <TextField
                  size="small"
                  label="Date"
                  type="date"
                  value={header.estimatedate}
                  disabled={isViewMode}
                  onChange={(e) => handleHeaderChange("estimatedate", e.target.value)}
                  sx={{ width: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Paper>

            {numericMode !== 1 && (
              <Paper sx={{ p: 1.5, borderRadius: 1, mb: 1.5 }} elevation={2}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1}>
                  <TextField
                    size="small"
                    type="date"
                    label="From Date"
                    value={searchFilters.fromdate}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => searchFilterChange("fromdate", e.target.value)}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To Date"
                    value={searchFilters.todate}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => searchFilterChange("todate", e.target.value)}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    size="small"
                    label="Estimate No"
                    value={searchFilters.estimateno}
                    onChange={(e) => searchFilterChange("estimateno", e.target.value)}
                    sx={{ width: 150 }}
                  />
                  <FormControl size="small" sx={{ width: 160 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={searchFilters.estimatetype}
                      onChange={(e) => searchFilterChange("estimatetype", e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="workshop">Workshop</MenuItem>
                      <MenuItem value="wholesale">Wholesale</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Customer / Vehicle / Mobile / Subledger"
                    value={searchFilters.text}
                    onChange={(e) => searchFilterChange("text", e.target.value)}
                    sx={{ width: 310 }}
                  />
                  <Button variant="contained" startIcon={<SearchIcon />} onClick={runSearch}>
                    Search
                  </Button>
                </Stack>
                <Box sx={{ height: 260, mt: 1.5 }}>
                  <DataGrid
                    rows={searchRows}
                    columns={searchColumns}
                    getRowId={(row) => row.estheaderid}
                    rowHeight={30}
                    headerHeight={36}
                    pageSize={5}
                    rowsPerPageOptions={[5, 10, 20]}
                  />
                </Box>
              </Paper>
            )}

            <Paper sx={{ p: 1.5, borderRadius: 1, mb: 1.5 }} elevation={2}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={estimateType === "workshop" ? 5 : 6}>
                  {estimateType === "workshop" ? (
                    <Autocomplete
                      size="small"
                      value={selectedJobcard}
                      options={jobcards}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        String(option.jobcardid) === String(value.jobcardid)
                      }
                      onChange={(event, value) => handleJobcardChange(value)}
                      disabled={isViewMode}
                      renderInput={(params) => <TextField {...params} label="Job Card / Customer" />}
                    />
                  ) : (
                    <Autocomplete
                      size="small"
                      value={selectedParty}
                      options={parties}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        String(option.subledgerid) === String(value.subledgerid)
                      }
                      onChange={(event, value) => handlePartyChange(value)}
                      disabled={isViewMode}
                      renderInput={(params) => <TextField {...params} label="Subledger" />}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label={estimateType === "workshop" ? "Customer Code" : "Subledger Code"}
                    value={header.customercode}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label={estimateType === "workshop" ? "Customer Name" : "Subledger Name"}
                    value={header.customername}
                    InputProps={{ readOnly: estimateType === "workshop" }}
                    disabled={isViewMode}
                    onChange={(e) => handleHeaderChange("customername", e.target.value)}
                  />
                </Grid>

                {estimateType === "workshop" && (
                  <>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Vehicle No"
                        value={header.vehicleno}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Mobile No"
                        value={header.mobileno}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Insurance Company"
                        value={header.insurancecompany}
                        disabled={isViewMode}
                        onChange={(e) => handleHeaderChange("insurancecompany", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Policy No"
                        value={header.policyno}
                        disabled={isViewMode}
                        onChange={(e) => handleHeaderChange("policyno", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Claim No"
                        value={header.claimno}
                        disabled={isViewMode}
                        onChange={(e) => handleHeaderChange("claimno", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Labour Charges"
                        value={header.labourCharge}
                        disabled={isViewMode}
                        onChange={(e) => handleHeaderChange("labourCharge", e.target.value)}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>

            <Paper sx={{ p: 1.5, borderRadius: 1 }} elevation={2}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1}>
                <TextField
                  size="small"
                  label="Part No"
                  value={newModePartNo}
                  inputRef={newPartNoRef}
                  disabled={isViewMode}
                  onChange={(e) => {
                    setNewModePartNo(e.target.value.toUpperCase());
                    setSelectedItem(null);
                    setPartno(null);
                    setStock(0);
                    setDelItem("");
                    setAltFlag("");
                  }}
                  onKeyDown={handlePartnoKey}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  label="Part Desc"
                  value={partno?.itemname || ""}
                  sx={{ width: 300 }}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Qty"
                  value={itemQty}
                  inputRef={quantityRef}
                  disabled={isViewMode}
                  onChange={(e) => setItemQty(e.target.value)}
                  onKeyDown={handleQtyKeyDown}
                  sx={{ width: 100 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Rate"
                  value={itemRate}
                  disabled={isViewMode}
                  onChange={(e) => setItemRate(e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  size="small"
                  label="Stock"
                  value={stock}
                  sx={{ width: 100 }}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Deleted Item"
                  value={delItem}
                  sx={{ width: 120 }}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Alternate"
                  value={altFlag}
                  sx={{ width: 110 }}
                  InputProps={{ readOnly: true }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  disabled={isViewMode}
                  onClick={handleAddItem}
                >
                  Add
                </Button>
              </Stack>

              {altparts.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                    Alternate Items
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                    {altparts.map((item) => (
                      <Button
                        key={`${item.alternateitemid || item.itemid}-${item.itemcode}`}
                        size="small"
                        variant="outlined"
                        onClick={() => handleAlternateItem(item)}
                      >
                        {item.itemcode || item.alternateitemcode} | Stock {item.closingqty || 0}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}

              <Box
                sx={{
                  height: 360,
                  mt: 1.5,
                  "& .MuiDataGrid-columnHeaders": { backgroundColor: "#5fc4df" },
                }}
              >
                <DataGrid
                  rows={rows}
                  columns={detailColumns}
                  getRowId={(row) => row.rowid}
                  rowHeight={28}
                  headerHeight={36}
                  disableSelectionOnClick
                  experimentalFeatures={{ newEditingApi: true }}
                />
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1.5}>
                <TextField
                  size="small"
                  label="Gross"
                  value={displayMoney(totals.gross)}
                  sx={{ width: 130 }}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="GST"
                  value={displayMoney(totals.gst)}
                  sx={{ width: 130 }}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Round Off"
                  value={displayMoney(totals.roundOff)}
                  sx={{ width: 130 }}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Total Value"
                  value={displayMoney(totals.billAmt)}
                  sx={{ width: 150 }}
                  InputProps={{ readOnly: true }}
                />
              </Stack>

              <Stack direction="row" justifyContent="center" spacing={2} mt={2}>
                <Button variant="outlined" onClick={resetForm}>
                  New
                </Button>
                <Button variant="contained" color="success" disabled={saving || isViewMode} onClick={saveEstimate}>
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  disabled={!loadedEstimate?.estheaderid}
                  onClick={printEstimate}
                >
                  Print
                </Button>
                <Button variant="outlined" color="warning" onClick={runSearch}>
                  Refresh
                </Button>
              </Stack>
            </Paper>

            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
              Workshop estimates use job card customer, vehicle and mobile data. Wholesale estimates use subledger data.
            </Typography>
          </Box>
        }
      />
    </div>
  );
};

export default Estimate;
